/*
  Device endpoints.

  Filtering, sorting and paging are server side parameters rather than something the table
  does to a fully downloaded list. At 10,000 endpoints, which is the number
  docs/00-architecture-overview.md sizes the gateway for, a client-side sort is not an
  option, and building the table against a server contract now means the mock and the real
  API stay interchangeable when the fixture set is 30 rows.
*/

import { request } from '../client';
import { isMockTransport } from '../config';
import type {
  ConnectSessionResponse,
  Device,
  DeviceDetail,
  DeviceListRow,
  DevicePlacementUpdate,
  DevicePlatform,
  DevicePresenceState,
  DeviceRebootRequest,
  DeviceRevokeRequest,
  DeviceTagsUpdate,
  Page,
  SessionCapability,
  Uuid,
} from '../types';

export const DEVICE_SORT_KEYS = [
  'name',
  'organizationName',
  'siteName',
  'os',
  'loggedInUser',
  'state',
  'lastSeenAt',
  'publicIp',
  'agentVersion',
  'lastTechnicianName',
] as const;
export type DeviceSortKey = (typeof DEVICE_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface DeviceListQuery {
  /** Free text across hostname, organization, site, user, IP, agent version and tags. */
  readonly search?: string;
  readonly states?: readonly DevicePresenceState[];
  readonly organizationIds?: readonly Uuid[];
  readonly platforms?: readonly DevicePlatform[];
  /** AND semantics: a device must carry every tag listed. */
  readonly tags?: readonly string[];
  readonly sort?: DeviceSortKey;
  readonly direction?: SortDirection;
  readonly limit?: number;
  readonly offset?: number;
}

/** Repeated query parameters are sent comma joined, which is what the Go handler parses. */
function toQueryParams(query: DeviceListQuery): Record<string, string | number | undefined> {
  return {
    search: query.search === '' ? undefined : query.search,
    states: query.states !== undefined && query.states.length > 0 ? query.states.join(',') : undefined,
    organizationIds:
      query.organizationIds !== undefined && query.organizationIds.length > 0
        ? query.organizationIds.join(',')
        : undefined,
    platforms:
      query.platforms !== undefined && query.platforms.length > 0 ? query.platforms.join(',') : undefined,
    tags: query.tags !== undefined && query.tags.length > 0 ? query.tags.join(',') : undefined,
    sort: query.sort,
    direction: query.direction,
    limit: query.limit,
    offset: query.offset,
  };
}

export async function listDevices(query: DeviceListQuery): Promise<Page<DeviceListRow>> {
  if (isMockTransport()) {
    const { mockListDevices } = await import('@/mocks/handlers');
    return mockListDevices(query);
  }
  return request<Page<DeviceListRow>>('/devices', { query: toQueryParams(query) });
}

export async function listDeviceTags(): Promise<readonly string[]> {
  if (isMockTransport()) {
    const { mockDeviceTags } = await import('@/mocks/handlers');
    return mockDeviceTags();
  }
  return request<readonly string[]>('/devices/tags');
}

/**
 * GET /devices/{id}. Overview, hardware inventory and activity in one response, for the
 * reason recorded on DeviceDetail in types.ts.
 */
export async function getDevice(deviceId: Uuid): Promise<DeviceDetail> {
  if (isMockTransport()) {
    const { mockGetDevice } = await import('@/mocks/handlers');
    return mockGetDevice(deviceId);
  }
  return request<DeviceDetail>(`/devices/${deviceId}`);
}

/**
 * PATCH /devices/{id}, placement only. Requires device.manage.
 *
 * Moving a device is one call rather than three, because organization, site and group have
 * to change together. A device whose site belongs to a different organization than its own
 * would pass every individual validation and still be a corrupt row.
 */
export async function moveDevice(
  deviceId: Uuid,
  placement: DevicePlacementUpdate,
): Promise<Device> {
  if (isMockTransport()) {
    const { mockMoveDevice } = await import('@/mocks/handlers');
    return mockMoveDevice(deviceId, placement);
  }
  return request<Device>(`/devices/${deviceId}`, { method: 'PATCH', body: placement });
}

/** PATCH /devices/{id}, tags only. Requires device.manage. */
export async function updateDeviceTags(
  deviceId: Uuid,
  update: DeviceTagsUpdate,
): Promise<Device> {
  if (isMockTransport()) {
    const { mockUpdateDeviceTags } = await import('@/mocks/handlers');
    return mockUpdateDeviceTags(deviceId, update);
  }
  return request<Device>(`/devices/${deviceId}`, { method: 'PATCH', body: update });
}

/**
 * POST /devices/{id}/reboot. Requires device.reboot, which is [dangerous], so the API also
 * requires an MFA assertion inside the last five minutes (docs/07-identity-rbac.md I.1).
 * The console cannot relax that and does not try to predict it: a step_up_required error
 * comes back as an ApiError kind the caller surfaces.
 */
export async function rebootDevice(
  deviceId: Uuid,
  body: DeviceRebootRequest,
): Promise<void> {
  if (isMockTransport()) {
    const { mockRebootDevice } = await import('@/mocks/handlers');
    return mockRebootDevice(deviceId, body);
  }
  return request<void>(`/devices/${deviceId}/reboot`, { method: 'POST', body });
}

/**
 * POST /devices/{id}/revoke. Requires device.manage, [dangerous].
 *
 * Semantics, from docs/05-windows-agent.md G.11 case 3: this invalidates the device
 * credential and force-closes the gateway connection whether or not the endpoint ever hears
 * about it. The binaries stay on disk until someone uninstalls them. The confirmation copy
 * in the UI says exactly that, because "revoke" reads like "uninstall" to most people and
 * it is not.
 */
export async function revokeDevice(
  deviceId: Uuid,
  body: DeviceRevokeRequest,
): Promise<Device> {
  if (isMockTransport()) {
    const { mockRevokeDevice } = await import('@/mocks/handlers');
    return mockRevokeDevice(deviceId, body);
  }
  return request<Device>(`/devices/${deviceId}/revoke`, { method: 'POST', body });
}

/**
 * POST /devices/{id}/connect, docs/03-protocol.md section E.5.
 *
 * The transport and the viewer are Phase 2. This call is wired to a mock as well as HTTP so
 * that the fixture transport does not throw a network error where the real one would return
 * a pending session, but nothing in the Phase 1 UI invokes it: the Remote Control button is
 * gated and says Phase 2. It exists so the Phase 2 viewer has a contract to build against
 * and so the shape here is reviewed now rather than invented later.
 *
 * The response carries a viewerToken with a 120 second life that is spent on the viewer
 * WebSocket connect and is useless against the REST API, so it is handed straight to the
 * viewer and never stored.
 */
export async function connectDevice(
  deviceId: Uuid,
  requestedCaps: readonly SessionCapability[],
): Promise<ConnectSessionResponse> {
  if (isMockTransport()) {
    const { mockConnectDevice } = await import('@/mocks/handlers');
    return mockConnectDevice(deviceId, requestedCaps);
  }
  return request<ConnectSessionResponse>(`/devices/${deviceId}/connect`, {
    method: 'POST',
    body: { requestedCaps },
  });
}
